import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useGymBillingOptional } from '../../contexts/GymBillingContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { getNavigationLinks, TRAINER_HOME_PATH } from '../../config/navigationConfig';
import GymLogo from './GymLogo';
import { ReactComponent as UserMenuIcon } from '../../assets/icons/menu-user.svg';
import { ReactComponent as LogoutMenuIcon } from '../../assets/icons/menu-logout.svg';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
} from 'lucide-react';
/** Allineato a SidebarLayout: sotto 1024px sidebar è drawer (chiusa di default) */
const MOBILE_BREAKPOINT = '(max-width: 1023px)';

const SidebarNavigation = () => {
  const { currentUser, logout, isGym, isTrainer, isAdmin } = useAuth();
  const billingCtx = useGymBillingOptional();
  const needsPlanNavGate = isGym() || isTrainer();
  const allowedPaths = billingCtx?.planAllowedPaths ?? billingCtx?.billing?.plan_allowed_paths ?? null;
  const navPlanReady =
    !needsPlanNavGate
    || (!billingCtx?.loading && Array.isArray(allowedPaths) && allowedPaths.length > 0);
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const { isCollapsed, toggleSidebar, isMobileDrawerOpen, closeMobileDrawer } = useSidebar();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  /** Drawer mobile o sidebar desktop compressa: solo icone, rail stretta */
  const narrowNav = isMobile || isCollapsed;
  const gymHomePath = isTrainer() ? TRAINER_HOME_PATH : '/gym';

  // Stato per menu espandibili (chiave: label del menu)
  const [expandedMenus, setExpandedMenus] = useState({});

  const { mainLinks, secondaryLinks } = navPlanReady
    ? getNavigationLinks(currentUser, isGym, isTrainer, isAdmin, { allowedPaths })
    : { mainLinks: [], secondaryLinks: [] };

  // Verifica se un link è attivo
  const isLinkActive = (to, exactMatch = false) => {
    if (!to) return false;
    if (exactMatch) {
      return location.pathname === to;
    }
    return location.pathname.startsWith(to);
  };

  // Verifica se un menu è attivo (se una delle voci interne è attiva)
  const isMenuActive = (children) => {
    if (!children) return false;
    return children.some(child => isLinkActive(child.to, child.exactMatch));
  };

  // Toggle espansione menu
  const toggleMenu = (menuLabel) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuLabel]: !prev[menuLabel]
    }));
  };

  // Auto-espandi menu se una voce interna è attiva (solo al mount o cambio route)
  useEffect(() => {
    const linksToExpand = {};
    [...mainLinks, ...secondaryLinks].forEach(link => {
      if (link.children && isMenuActive(link.children)) {
        const menuLabel = link.label;
        linksToExpand[menuLabel] = true;
      }
    });
    
    // Aggiorna solo se ci sono cambiamenti
    if (Object.keys(linksToExpand).length > 0) {
      setExpandedMenus(prev => {
        const hasChanges = Object.keys(linksToExpand).some(key => !prev[key]);
        if (hasChanges) {
          return { ...prev, ...linksToExpand };
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Solo quando cambia la route

  // Render singolo link normale
  const renderLink = (link) => {
    const Icon = link.icon;
    const isActive = isLinkActive(link.to, link.exactMatch);
    const label = t(link.label);
    const iconColorClass = isDark ? (isActive ? 'text-white' : 'text-gray-400') : (isActive ? 'text-[#372d83]' : 'text-[#9e96c7]');

    return (
      <Link
        key={link.to}
        to={link.to}
        className={`
          flex items-center gap-3 px-fluid-xs py-3 rounded-lg transition-all duration-200
          ${isActive 
            ? 'bg-[#bba8e3] dark:bg-indigo-600 text-[#3a3a3a] dark:text-white shadow-lg' 
            : 'text-[#3a3a3a] dark:text-gray-300 hover:bg-[#d1cce2] dark:hover:bg-gray-800'
          }
          ${narrowNav ? 'justify-center' : ''}
        `}
        title={narrowNav ? label : ''}
      >
        <Icon className={`h-5 w-5 flex-shrink-0 ${iconColorClass}`} />
        {!narrowNav && (
          <span className="text-sm font-medium truncate">{label}</span>
        )}
      </Link>
    );
  };

  // Render menu espandibile
  const renderExpandableMenu = (link) => {
    const MenuIcon = link.icon;
    const menuLabel = t(link.label);
    const isExpanded = expandedMenus[menuLabel] || false;
    const hasActiveChild = isMenuActive(link.children);
    const menuKey = `menu-${link.label}`;
    const menuIconColorClass = isDark ? (hasActiveChild ? 'text-white' : 'text-gray-400') : (hasActiveChild ? 'text-[#372d83]' : 'text-[#9e96c7]');

    // Sidebar compressa o drawer mobile: solo icona (come link normale, senza espansione)
    if (narrowNav) {
      // Trova il primo child o quello attivo
      const activeChild = link.children?.find(child => isLinkActive(child.to));
      const defaultChild = link.children?.[0];
      const targetLink = activeChild || defaultChild;

      if (targetLink) {
        const ChildIcon = targetLink.icon;
        const isActive = isLinkActive(targetLink.to);
        return (
          <Link
            key={menuKey}
            to={targetLink.to}
            className={`
              flex items-center justify-center px-fluid-xs py-3 rounded-lg transition-all duration-200
              ${isActive 
                ? 'bg-[#bba8e3] dark:bg-indigo-600 text-[#3a3a3a] dark:text-white shadow-lg' 
                : 'text-[#3a3a3a] dark:text-gray-300 hover:bg-[#d1cce2] dark:hover:bg-gray-800'
              }
            `}
            title={t(targetLink.label)}
          >
            <ChildIcon className={`h-5 w-5 flex-shrink-0 ${isDark ? (isActive ? 'text-white' : 'text-gray-400') : (isActive ? 'text-[#372d83]' : 'text-[#9e96c7]')}`} />
          </Link>
        );
      }
    }

    // Sidebar espansa: mostra menu con expand/collapse
    return (
      <div key={menuKey} className="space-y-1">
        {/* Pulsante menu principale */}
        <button
          onClick={() => toggleMenu(menuLabel)}
          className={`
            w-full flex items-center gap-3 px-fluid-xs py-3 rounded-lg transition-all duration-200
            ${hasActiveChild
              ? 'bg-[#bba8e3] dark:bg-indigo-600 text-[#3a3a3a] dark:text-white shadow-lg' 
              : 'text-[#3a3a3a] dark:text-gray-300 hover:bg-[#d1cce2] dark:hover:bg-gray-800'
            }
          `}
          title={narrowNav ? menuLabel : ''}
        >
          <MenuIcon className={`h-5 w-5 flex-shrink-0 ${menuIconColorClass}`} />
          <span className="text-sm font-medium truncate flex-1 text-left">{menuLabel}</span>
          <ChevronDown 
            className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            } ${hasActiveChild ? 'text-[#3a3a3a] dark:text-white' : ''}`}
          />
        </button>

        {/* Voci del menu (animate) */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="ml-4 space-y-1 border-l-2 border-gray-200 py-1 pl-4 dark:border-white/15">
                {link.children.map(child => {
                  const ChildIcon = child.icon;
                  const isActive = isLinkActive(child.to, child.exactMatch);
                  const childLabel = t(child.label);

                  return (
                    <Link
                      key={child.to}
                      to={child.to}
                      className={`
                        flex items-center gap-3 px-fluid-xs py-2 rounded-lg transition-all duration-200
                        ${isActive
                          ? 'bg-[#bba8e3] dark:bg-indigo-600 text-[#3a3a3a] dark:text-white shadow-md' 
                          : 'text-[#3a3a3a] dark:text-white/65 hover:bg-[#d1cce2] dark:hover:bg-gray-800'
                        }
                      `}
                    >
                      <ChildIcon className={`h-4 w-4 flex-shrink-0 ${isDark ? (isActive ? 'text-white' : 'text-gray-400') : (isActive ? 'text-[#372d83]' : 'text-[#9e96c7]')}`} />
                      <span className="text-sm font-medium truncate">{childLabel}</span>
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Render item (link normale o menu espandibile)
  const renderMenuItem = (link) => {
    if (link.children && link.children.length > 0) {
      return renderExpandableMenu(link);
    }
    return renderLink(link);
  };

  /** Larghezza rail: in sync con sidebarDimensions.js (SIDEBAR_RAIL_WIDTH_PX) */
  const asideWidthClass = isCollapsed ? 'w-[96px]' : 'w-[280px]';
  const drawerOpen = isMobile && isMobileDrawerOpen;

  return (
    <>
      {/* Su mobile: overlay per chiudere il drawer al tap fuori (WEBAPP-033) */}
      {isMobile && (
        <div
          role="button"
          tabIndex={0}
          aria-label={t('gym.layout.closeMenu', 'Chiudi menu')}
          className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 md:hidden ${
            drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeMobileDrawer}
          onKeyDown={(e) => e.key === 'Escape' && closeMobileDrawer()}
        />
      )}

      <aside
        className={`
          fixed left-0 top-0 h-screen bg-[#e5dff0] dark:bg-gray-900
          border-r border-gray-200 dark:border-gray-800
          transition-all duration-300 ease-in-out z-50
          ${isMobile ? `w-[96px] max-w-[96px] ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}` : asideWidthClass}
        `}
        aria-hidden={isMobile ? !drawerOpen : undefined}
      >
        <div className="relative flex flex-col h-full">
          {/* Header con Logo (su mobile anche pulsante Chiudi) */}
          <div
            className={`
            flex border-b border-gray-200 dark:border-gray-800
            ${isMobile ? 'flex-col items-center gap-2 py-3 px-1' : 'items-center px-fluid-sm py-5 justify-between'}
            ${!isMobile && isCollapsed ? 'justify-center' : ''}
          `}
          >
            <Link
              to={gymHomePath}
              className={`flex items-center min-w-0 ${isMobile ? 'justify-center' : 'flex-1'}`}
              onClick={isMobile ? closeMobileDrawer : undefined}
            >
              <GymLogo
                variant={narrowNav ? 'icon' : 'full'}
                className={isDark ? 'text-white' : 'text-[#372d83]'}
              />
            </Link>

            {isMobile && (
              <button
                type="button"
                onClick={closeMobileDrawer}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-white/10"
                aria-label={t('gym.layout.closeMenu', 'Chiudi menu')}
              >
                <X className="h-5 w-5 text-[#3a3a3a] dark:text-white/85" />
              </button>
            )}
          </div>

          {/* Linguetta laterale: toggle collapse (solo desktop) */}
          {!isMobile && (
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label={isCollapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
              title={isCollapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
              className={`
                absolute top-5 -right-3 z-10
                h-10 w-6 rounded-r-xl rounded-l-md
                flex items-center justify-center
                border border-gray-200 dark:border-gray-800
                bg-[#e5dff0] text-[#3a3a3a] shadow-md
                dark:bg-gray-900 dark:text-white
                hover:brightness-105 active:brightness-95 dark:hover:bg-gray-800
                transition
              `}
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
          )}

        {/* Main Navigation Links */}
        <nav className={`flex-1 overflow-y-auto space-y-1 sidebar-menu-font ${isMobile ? 'px-1 py-2' : 'p-fluid-sm'}`}>
          {!navPlanReady ? (
            <div className="space-y-2 px-1" aria-hidden="true">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div
                  key={i}
                  className={`h-10 rounded-lg animate-pulse ${
                    isDark ? 'bg-gray-800' : 'bg-[#d1cce2]/80'
                  } ${narrowNav ? 'mx-auto w-10' : 'w-full'}`}
                />
              ))}
            </div>
          ) : (
            <>
              {mainLinks.map((link) => renderMenuItem(link))}
              {secondaryLinks.length > 0 && (
                <div className="my-4 border-t border-gray-200 dark:border-white/10" />
              )}
              {secondaryLinks.map((link) => renderMenuItem(link))}
            </>
          )}
        </nav>

        {/* Bottom Section: utente e logout. Quando sidebar compressa: colonna centrata. */}
        <div className={`space-y-2 border-t border-gray-200 dark:border-white/10 ${isMobile ? 'px-1 py-2' : 'p-fluid-sm'} ${narrowNav ? 'flex flex-col items-center' : ''}`}>
          {/* Ultima riga: Utente + Logout (compressa: solo icona utente + icona logout centrate) */}
          <div className={`
            flex items-center gap-2 rounded-lg bg-[#d1cce2] dark:bg-gray-800
            ${narrowNav ? 'justify-center py-2' : 'px-3 py-2'}
          `}>
            <div className={`flex items-center gap-2 min-w-0 flex-1 ${narrowNav ? 'justify-center flex-1' : ''}`}>
              <UserMenuIcon className={`h-5 w-5 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-[#9e96c7]'}`} aria-hidden />
              {!narrowNav && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#3a3a3a] dark:text-white truncate">
                    {currentUser?.name || 'User'}
                  </p>
                  <p className="truncate text-xs text-[#3a3a3a] dark:text-white/55">
                    {currentUser?.email || ''}
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={logout}
              className="p-2 rounded-lg text-[#cb171a] hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
              title={t('gym.navigation.logout')}
              aria-label={t('gym.navigation.logout')}
            >
              <LogoutMenuIcon className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </aside>
    </>
  );
};

export default SidebarNavigation;

